'use client';

import { useCallback, useEffect, useState } from 'react';
import { Bookmark } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { deleteAccountSavedItem, getAccountSavedStatus, saveAccountSavedItem } from '@/hooks/use-account-data';
import { useAppAuth } from '@/hooks/use-app-auth';
import { ROUTES } from '@/lib/routes';
import type { AccountSavedItemDoc, AccountSavedItemType } from '@/types/platform';

type SaveAccountItemButtonProps = {
  itemType: AccountSavedItemType;
  itemId: string;
  title?: string;
  href?: string;
  description?: string;
  imageUrl?: string;
  imageAlt?: string;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
};

function loginHref() {
  if (typeof window === 'undefined') return ROUTES.login;
  return `${ROUTES.login}?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`;
}

export function SaveAccountItemButton({
  itemType,
  itemId,
  title,
  href,
  description,
  imageUrl,
  imageAlt,
  className,
  variant = 'outline',
  size = 'default',
}: SaveAccountItemButtonProps) {
  const { user, session, loading: authLoading } = useAppAuth();
  const [saved, setSaved] = useState<AccountSavedItemDoc | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!user && !session) {
      setSaved(null);
      return;
    }
    try {
      setStatusLoading(true);
      const result = await getAccountSavedStatus({ itemType, itemId });
      setSaved(result.saved ?? null);
      setMessage(null);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to check saved status.');
    } finally {
      setStatusLoading(false);
    }
  }, [itemId, itemType, session, user]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void loadStatus();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [loadStatus]);

  async function handleClick() {
    if (!user && !session) {
      window.location.assign(loginHref());
      return;
    }
    try {
      setWorking(true);
      setMessage(null);
      if (saved?.savedId) {
        await deleteAccountSavedItem({ savedId: saved.savedId });
        setSaved(null);
        setMessage('Removed from saved.');
        return;
      }
      const result = await saveAccountSavedItem({ itemType, itemId, title, href, description, imageUrl, imageAlt });
      setSaved(result.saved ?? null);
      setMessage('Saved to your account.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to update saved items.');
    } finally {
      setWorking(false);
    }
  }

  const disabled = authLoading || statusLoading || working;
  const label = saved ? 'Saved' : 'Save';

  return (
    <span className="inline-flex flex-col gap-2">
      <Button type="button" variant={variant} size={size} className={className} disabled={disabled} onClick={() => void handleClick()}>
        <Bookmark className="mr-2 h-4 w-4" fill={saved ? 'currentColor' : 'none'} />
        {working ? 'Saving...' : label}
      </Button>
      {message ? <span className="text-xs text-charcoal/65">{message}</span> : null}
    </span>
  );
}
